import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import BackOfficeLayout from "./BackOfficeLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  FileText,
  Search,
  Filter,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowUpRight,
  Loader2,
  DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackOfficeApplications = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusCounts, setStatusCounts] = useState({});

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      
      const response = await axios.get(`${API}/backoffice/applications?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setApplications(response.data.applications || []);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: "bg-yellow-100 text-yellow-700", icon: Clock, label: "Pending" },
      under_review: { color: "bg-blue-100 text-blue-700", icon: Eye, label: "Under Review" },
      recommended: { color: "bg-indigo-100 text-indigo-700", icon: FileText, label: "Recommended" },
      escalated: { color: "bg-orange-100 text-orange-700", icon: ArrowUpRight, label: "Escalated" },
      approved: { color: "bg-green-100 text-green-700", icon: CheckCircle, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" }
    };
    
    const config = configs[status] || { color: "bg-gray-100 text-gray-700", icon: AlertCircle, label: status };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      app.application_id?.toLowerCase().includes(search) ||
      app.business_name?.toLowerCase().includes(search) ||
      app.institution_name?.toLowerCase().includes(search) ||
      app.full_name?.toLowerCase().includes(search) ||
      app.user_email?.toLowerCase().includes(search)
    );
  });

  const getApplicantName = (app) => {
    return app.business_name || app.institution_name || app.full_name || app.user_name || "N/A";
  };

  const canTakeAction = (app) => {
    const role = user?.role;
    const status = app.status;
    const amount = app.amount || app.amount_required || 0;

    if (role === "loan_officer") {
      return status === "pending" || status === "under_review";
    }
    if (role === "manager") {
      return status === "recommended";
    }
    if (role === "executive") {
      return status === "escalated";
    }
    if (role === "admin") {
      return true;
    }
    return false;
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Loan Applications</h1>
            <p className="text-gray-600">Review and process loan applications</p>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { key: "pending", label: "Pending", color: "border-yellow-400" },
            { key: "recommended", label: "Recommended", color: "border-indigo-400" },
            { key: "escalated", label: "Escalated", color: "border-orange-400" },
            { key: "approved", label: "Approved", color: "border-green-400" },
            { key: "rejected", label: "Rejected", color: "border-red-400" }
          ].map(({ key, label, color }) => (
            <Card 
              key={key} 
              className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${color} ${statusFilter === key ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
            >
              <CardContent className="p-4">
                <p className="text-2xl font-bold">{statusCounts[key] || 0}</p>
                <p className="text-sm text-gray-600">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by ID, name, or email..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="recommended">Recommended</SelectItem>
                  <SelectItem value="escalated">Escalated</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No applications found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Application ID</TableHead>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApplications.map((app) => (
                      <TableRow 
                        key={app.application_id} 
                        data-testid={`app-row-${app.application_id}`}
                        className={canTakeAction(app) ? "bg-blue-50/30" : ""}
                      >
                        <TableCell className="font-mono text-sm">
                          {app.application_id?.substring(0, 15)}...
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{getApplicantName(app)}</p>
                            <p className="text-sm text-gray-500">{app.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{app.loan_type?.replace(/_/g, " ") || app.applicant_type}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-green-700">
                            {formatCurrency(app.amount || app.amount_required)}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(app.status)}</TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {formatDate(app.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant={canTakeAction(app) ? "default" : "outline"}
                            onClick={() => navigate(`/backoffice/review/${app.application_id}`)}
                            data-testid={`review-btn-${app.application_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {canTakeAction(app) ? "Review" : "View"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeApplications;
